import * as React from 'react'
import PropTypes from 'prop-types'
import {get, forEach} from 'lodash'
import moment from 'moment'
import dynamic from 'next/dynamic'
import Prism from 'prismjs'

import {t, axios, stripTags} from '@lib/helpers'
import {logEvent} from '@lib/gtm'

const MainLayout = dynamic(() => import('@components/MainLayout'))
const Meta = dynamic(() => import('@components/Meta'))
const BlogItem = dynamic(() => import('@components/BlogItem'))
const Custom404 = dynamic(() => import('@components/Custom404'))
import {ContactForm} from '@components/blogs/ContactForm'

export default function BlogShow({blog}) {
  if (get(blog, '_status')) {
    return <Custom404 />
  }

  const $markdown = React.useRef()

  React.useEffect(() => {
    if ($markdown.current) {
      forEach($markdown.current.querySelectorAll('pre code'), (x) => {
        x.innerHTML = Prism.highlight(
          x.innerHTML
            .replace(/&lt;/gi, '<')
            .replace(/&gt;/gi, '>')
            .replace(/&amp;/gi, '&'),
          Prism.languages.javascript,
          'javascript'
        )
      })
    }
  }, [get(blog, 'id')])

  const blogMetaTitle = get(blog, 'th_meta_title') || get(blog, 'th_title')

  return (
    <React.Fragment>
      <Meta
        title={`${blogMetaTitle} – ${stripTags(t('pages.blogs.title'))} – ${t(
          'layout.title'
        )}`}
        description={get(blog, 'th_meta_description')}
        image={get(blog, 'image_1.url')}
        imageExternal
      />

      <div className='BlogsShow'>
        <div className='BodySpace'>
          <div className='BlogsShow__main'>
            <div className='container'>
              <h1 className='BlogsShow__h2'>{get(blog, 'th_title')}</h1>

              <time className='BlogsShow__time'>
                {moment(get(blog, 'created_at')).format('MMMM D, YYYY')}
              </time>

              {get(blog, 'category_id') && (
                <a
                  className='BlogsShow__category'
                  href={`/blogs?category_id=${get(blog, 'category_id')}`}
                  onClick={() => {
                    logEvent({
                      ga: {
                        category: 'Blog',
                        action: `Click ${
                          t('pages.blogs.nav')[get(blog, 'category_id')]
                        }`,
                      },
                    })
                  }}
                >
                  {t('pages.blogs.nav')[get(blog, 'category_id')]}
                </a>
              )}

              {get(blog, 'image_1.url') && (
                <img
                  className='BlogsShow__cover'
                  src={get(blog, 'image_1.url')}
                  width='690'
                  height='400'
                  alt={blogMetaTitle}
                  loading='lazy'
                />
              )}

              <div
                className='Markdown'
                ref={$markdown}
                dangerouslySetInnerHTML={{__html: get(blog, 'th_content')}}
              />
            </div>
          </div>

          <div className="BlogsShow__contact">
            <div className='container'>
              <ContactForm title="ติดต่อเรา" />
            </div>
          </div>

          <div className='BlogsShow__related'>
            <div className='container'>
              <h5 className='BlogsShow__related__h5'>
                {t('pages.blogsShow.h5')}
              </h5>

              <dl className='Index__know__blogs__dl'>
                {(get(blog, 'th_recommended') || []).map((x, i) => (
                  <BlogItem key={i} data={x} />
                ))}
              </dl>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  )
}

BlogShow.propTypes = {
  blog: PropTypes.object,
}

export const getServerSideProps = async (ctx) => {
  let blog

  try {
    const slug = encodeURI(get(ctx, 'query.id'))

    blog = await axios.get(`/th_blogs/${slug}`)
  } catch (e) {
    const _status = get(e, 'response.status')

    if (ctx.res) {
      ctx.res.statusCode = _status
    }

    return {
      props: {
        blog: {
          _status,
        },
      },
    }
  }

  return {
    props: {
      blog: get(blog, 'data.data'),
      query: get(ctx, 'query'),
    },
  }
}

BlogShow.getLayout = function getLayout(page) {
  return <MainLayout>{page}</MainLayout>
}
